import axios from 'axios';
import Cookies from 'js-cookie';
import { Auth } from './auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/research';

function getCsrfToken() {
    return Cookies.get('csrftoken') || '';
}

const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true, // Enable sending cookies for session-based auth
});

apiClient.interceptors.request.use(
    (config) => {
        const csrfToken = getCsrfToken();
        if (csrfToken && ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
            config.headers['X-CSRFToken'] = csrfToken;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

function transformGraphQLFilter(filter) {
    const params = {};
    for (const [key, value] of Object.entries(filter)) {
        if (value && typeof value === 'object') {
            if ('eq' in value) params[key] = value.eq;
            else if ('contains' in value) params[`${key}__icontains`] = value.contains;
            else if ('matchPhrasePrefix' in value) params.q = value.matchPhrasePrefix;
            else if ('gte' in value && 'lte' in value) {
                params[`${key}__gte`] = value.gte;
                params[`${key}__lte`] = value.lte;
            } else if ('gte' in value) {
                params[`${key}__gte`] = value.gte;
            } else if ('lte' in value) {
                params[`${key}__lte`] = value.lte;
            }
        } else if (value !== undefined && value !== null && value !== '') {
            params[key] = value;
        }
    }
    return params;
}

apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        console.error('API Error:', error.response?.data || error.message);
        return Promise.reject(error);
    }
);

export const projectsAPI = {
    list: async (params = {}) => {
        const response = await apiClient.get('/projects/', { params });
        return { data: { listProjects: { items: response.data.items || [], total: response.data.total || 0 } } };
    },

    getById: async (id) => {
        const response = await apiClient.get(`/projects/${id}/`);
        return { data: { getProject: response.data } };
    },

    get: async (pkOrId, SK) => {
        if (SK === undefined) {
            return projectsAPI.getById(pkOrId);
        }
        const response = await apiClient.get('/projects/', { params: { PK: pkOrId, SK } });
        const items = response.data.items || response.data.results || response.data;
        return { data: { getProject: Array.isArray(items) ? items[0] || null : items } };
    },

    create: async (input) => {
        const response = await apiClient.post('/projects/', input);
        return { data: { createProject: response.data } };
    },

    update: async (input) => {
        const { id, PK, SK, ...updateData } = input;
        if (id) {
            const response = await apiClient.patch(`/projects/${id}/`, updateData);
            return { data: { updateProject: response.data } };
        }
        if (!PK || !SK) {
            throw new Error('Project ID or PK/SK is required for update');
        }
        const getResponse = await apiClient.get('/projects/', { params: { PK, SK } });
        const items = getResponse.data.items || getResponse.data.results || getResponse.data;
        const projectId = Array.isArray(items) ? items[0]?.id : items?.id;
        if (!projectId) {
            throw new Error('Project ID not found for PK/SK: ' + PK + '/' + SK);
        }
        const response = await apiClient.patch(`/projects/${projectId}/`, updateData);
        return { data: { updateProject: response.data } };
    },

    delete: async (idOrPK, options = {}) => {
        const params = {};
        if (options.userID) params.userID = options.userID;
        
        if (typeof options === 'string') {
            // Legacy: second param is SK
            const SK = options;
            const getResponse = await apiClient.get('/projects/', { params: { PK: idOrPK, SK } });
            const items = getResponse.data.results || getResponse.data;
            const projectId = items[0]?.id;
            await apiClient.delete(`/projects/${projectId}/`, { params });
            return { data: { deleteProject: { PK: idOrPK, SK } } };
        }
        
        await apiClient.delete(`/projects/${idOrPK}/`, { params });
        return { data: { deleteProject: { id: idOrPK } } };
    },

    search: async (filter = {}, options = {}) => {
        const params = {};
        
        for (const [key, value] of Object.entries(filter)) {
            if (value && typeof value === 'object') {
                if ('eq' in value) params[key] = value.eq;
                else if ('contains' in value) params[`${key}__icontains`] = value.contains;
                else if ('matchPhrasePrefix' in value) params.q = value.matchPhrasePrefix;
                else if ('gte' in value && 'lte' in value) {
                    params[`${key}__gte`] = value.gte;
                    params[`${key}__lte`] = value.lte;
                } else if ('gte' in value) {
                    params[`${key}__gte`] = value.gte;
                } else if ('lte' in value) {
                    params[`${key}__lte`] = value.lte;
                }
            } else if (value !== undefined && value !== null && value !== '') {
                params[key] = value;
            }
        }
        
        if (options.from !== undefined) params.offset = options.from;
        if (options.limit !== undefined) params.limit = options.limit;
        if (options.sort) {
            const dir = options.sort.direction === 'desc' ? '-' : '';
            params.ordering = `${dir}${options.sort.field}`;
        }
        
        const response = await apiClient.get('/projects/', { params });
        return { data: { searchProjects: { items: response.data.items || response.data.results || [], total: response.data.total || 0 } } };
    },
};

export const attributesAPI = {
    list: async (params = {}) => {
        const response = await apiClient.get('/attributes/', { params });
        // DRF returns {items: [...], total: ...} directly in response.data
        const items = response.data.items || response.data.results || (Array.isArray(response.data) ? response.data : []);
        const total = response.data.total || items.length;
        return { data: { listAttributes: { items, total } } };
    },

    getById: async (id) => {
        const response = await apiClient.get(`/attributes/${id}/`);
        return { data: { getAttribute: response.data } };
    },

    get: async (idOrPK) => {
        if (typeof idOrPK === 'number' || (typeof idOrPK === 'string' && /^\d+$/.test(idOrPK))) {
            return attributesAPI.getById(idOrPK);
        }
        const response = await apiClient.get('/attributes/', { params: { PK: idOrPK } });
        const items = response.data.items || response.data.results || response.data;
        return { data: { getAttribute: Array.isArray(items) ? items[0] || null : items } };
    },

    create: async (input) => {
        const { SK, ...createData } = input;
        const response = await apiClient.post('/attributes/', createData);
        return { data: { createAttribute: response.data } };
    },

    update: async (input) => {
        const { id, PK, SK, ...updateData } = input;
        if (id) {
            const response = await apiClient.patch(`/attributes/${id}/`, updateData);
            return { data: { updateAttribute: response.data } };
        }
        if (!PK) {
            throw new Error('Attribute ID or PK is required for update');
        }
        const getResponse = await apiClient.get('/attributes/', { params: { PK } });
        const items = getResponse.data.items || getResponse.data.results || getResponse.data;
        const attrId = Array.isArray(items) ? items[0]?.id : items?.id;
        if (!attrId) {
            throw new Error('Attribute ID not found for PK: ' + PK);
        }
        const response = await apiClient.patch(`/attributes/${attrId}/`, updateData);
        return { data: { updateAttribute: response.data } };
    },

    delete: async (idOrPK) => {
        if (typeof idOrPK === 'number' || (typeof idOrPK === 'string' && /^\d+$/.test(idOrPK))) {
            await apiClient.delete(`/attributes/${idOrPK}/`);
            return { data: { deleteAttribute: { id: idOrPK } } };
        }
        const getResponse = await apiClient.get('/attributes/', { params: { PK: idOrPK } });
        const items = getResponse.data.items || getResponse.data.results || getResponse.data;
        const attrId = Array.isArray(items) ? items[0]?.id : items?.id;
        if (!attrId) {
            throw new Error('Attribute ID not found for PK: ' + idOrPK);
        }
        await apiClient.delete(`/attributes/${attrId}/`);
        return { data: { deleteAttribute: { id: attrId } } };
    },

    search: async (params = {}) => {
        const response = await apiClient.get('/attributes/', { params });
        return { data: { searchAttributes: { items: response.data.items || response.data.results || [], total: response.data.total || 0 } } };
    },
};

export const logsAPI = {
    list: async (params = {}) => {
        const response = await apiClient.get('/logs/', { params });
        return { data: { listLogs: { items: response.data.items || [], total: response.data.total || 0 } } };
    },

    getById: async (id) => {
        const response = await apiClient.get(`/logs/${id}/`);
        return { data: { getLog: response.data } };
    },

    get: async (pkOrId, Date) => {
        if (Date === undefined) {
            return logsAPI.getById(pkOrId);
        }
        const response = await apiClient.get('/logs/', { params: { PK: pkOrId, Date } });
        const items = response.data.results || response.data;
        return { data: { getLog: items[0] || null } };
    },

    create: async (input) => {
        const response = await apiClient.post('/logs/', input);
        return { data: { createLog: response.data } };
    },

    update: async (id, input) => {
        const response = await apiClient.patch(`/logs/${id}/`, input);
        return { data: { updateLog: response.data } };
    },

    delete: async (id) => {
        await apiClient.delete(`/logs/${id}/`);
        return { data: { deleteLog: { id } } };
    },

    search: async (params = {}) => {
        const response = await apiClient.get('/logs/', { params });
        return { data: { searchLogs: { items: response.data.results || response.data, total: response.data.count || response.data.length } } };
    },
};

export const doiRequestsAPI = {
    list: async (params = {}) => {
        const response = await apiClient.get('/doi-requests/', { params });
        return { data: { listDOIRequests: { items: response.data.items || [], total: response.data.total || 0 } } };
    },

    getById: async (id) => {
        const response = await apiClient.get(`/doi-requests/${id}/`);
        return { data: { getDOIRequest: response.data } };
    },

    create: async (input) => {
        const response = await apiClient.post('/doi-requests/', input);
        return { data: { createDOIRequest: response.data } };
    },

    update: async (id, input) => {
        const response = await apiClient.patch(`/doi-requests/${id}/`, input);
        return { data: { updateDOIRequest: response.data } };
    },

    delete: async (id) => {
        await apiClient.delete(`/doi-requests/${id}/`);
        return { data: { deleteDOIRequest: { id } } };
    },
};

export const blacklistAPI = {
    list: async (params = {}) => {
        const response = await apiClient.get('/blacklist/', { params });
        return { data: { getBlacklistsByEmail: { items: response.data.items || [], total: response.data.total || 0 } } };
    },

    create: async (input) => {
        const response = await apiClient.post('/blacklist/', input);
        return { data: response.data };
    },

    delete: async (id) => {
        await apiClient.delete(`/blacklist/${id}/`);
        return { data: { success: true } };
    },
};

export const API = {
    // Direct API methods - no more GraphQL
    listProjects: projectsAPI.list,
    getProject: projectsAPI.get,
    createProject: projectsAPI.create,
    updateProject: projectsAPI.update,
    deleteProject: projectsAPI.delete,

    listAttributes: attributesAPI.list,
    getAttribute: attributesAPI.get,
    createAttribute: attributesAPI.create,
    updateAttribute: attributesAPI.update,
    deleteAttribute: attributesAPI.delete,

    listLogs: logsAPI.list,
    getLog: logsAPI.get,
    createLog: logsAPI.create,

    listDOIRequests: doiRequestsAPI.list,
    getDOIRequest: doiRequestsAPI.getById,
    createDOIRequest: doiRequestsAPI.create,
    updateDOIRequest: doiRequestsAPI.update,
    deleteDOIRequest: doiRequestsAPI.delete,

    listBlacklist: blacklistAPI.list,
    createBlacklist: blacklistAPI.create,
    deleteBlacklist: blacklistAPI.delete,

    projects: projectsAPI,
    attributes: attributesAPI,
    logs: logsAPI,
    doiRequests: doiRequestsAPI,
    blacklist: blacklistAPI,
};

export { apiClient };
export default API;

